import * as React from 'react';

/**
 * JRC 05/13/26 CO-16108
 */

// Add CSS for the segment inputs and calendar popup. We do this via JS instead
function ensureSegmentStylesInjected(): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById('ecp-datetime-segment-style')) return;
    const style = document.createElement('style');
    style.id = 'ecp-datetime-segment-style';
    style.textContent = `
        .ecp-datetime-segment:focus { text-align: left !important; }
        .ecp-datetime-segment:focus::placeholder { color: transparent !important; }
        .ecp-datetime-segment:focus::-webkit-input-placeholder { color: transparent !important; }
        .ecp-datetime-segment:focus::-moz-placeholder { color: transparent !important; opacity: 0; }
        .ecp-datetime-segment:focus:-ms-input-placeholder { color: transparent !important; }

        /* Calendar popup: darken every button's outline on hover so users
           get a clear hit-target affordance. Day buttons use a transparent
           1px border by default so this hover swap doesn't shift layout. */
        .ecp-cal-btn { transition: border-color 0.1s ease, background 0.1s ease; }
        .ecp-cal-btn:hover:not(:disabled) {
            border-color: #555 !important;
        }
        .ecp-cal-day-btn:hover:not(:disabled) {
            border-color: #555 !important;
            background: #eef5fb !important;
        }
        /* Don't fight the "selected day" blue highlight on hover. */
        .ecp-cal-day-btn.ecp-cal-day-selected:hover:not(:disabled) {
            background: #1976d2 !important;
        }

        /* Now / Clear buttons next to the field box. Darken outline + bg
           on hover to match the calendar buttons. */
        .ecp-datetime-now-btn,
        .ecp-datetime-clear-btn {
            border: 1px solid #ccc;
            background: #f5f5f5;
            transition: border-color 0.1s ease, background 0.1s ease;
        }
        .ecp-datetime-now-btn:hover:not(:disabled),
        .ecp-datetime-clear-btn:hover:not(:disabled) {
            border-color: #555 !important;
            background: #e8e8e8 !important;
        }

        /* Calendar-icon button — darken its (transparent) outline on
           hover so users see it's clickable. */
        .ecp-datetime-calendar-btn {
            border: 1px solid transparent !important;
            border-radius: 3px;
            transition: border-color 0.1s ease, background 0.1s ease;
        }
        .ecp-datetime-calendar-btn:hover:not(:disabled) {
            border-color: #555 !important;
            background: #eef5fb !important;
        }
    `;
    document.head.appendChild(style);
}

export type DateTimeKind = 'date' | 'dateTime' | 'time';

export interface ReactDateTimeInputProps {
    id?: string;
    kind?: DateTimeKind;
    value: string;
    disabled?: boolean;
    required?: boolean;
    min?: string;
    max?: string;
    css?: string;
    style?: React.CSSProperties;
    placeholder?: string;
    title?: string;
    showClearButton?: boolean;
    militaryTime?: boolean;
    allowMilitaryTimeEntry?: boolean;
    calendarHeaderClassName?: string;
    calendarHeaderStyle?: React.CSSProperties;
    rangeRole?: 'start' | 'end';
    rangeCounterpartValue?: string;
    rangeInvalidTitle?: string;
    onChange?: (newValue: string) => void;
    onBlur?: (currentValue: string) => void;
}

type SegmentKey = 'month' | 'day' | 'year' | 'hour' | 'minute' | 'second' | 'ampm';

interface ReactDateTimeInputState {
    month: string;
    day: string;
    year: string;
    hour: string;   // 12-hour (1-12) as 1- or 2-digit string
    minute: string;
    second: string;
    ampm: string;   // '' | 'AM' | 'PM'
    calendarOpen: boolean;
    calYear: number;
    calMonth: number;
}

export class ReactDateTimeInput
    extends React.Component<ReactDateTimeInputProps, ReactDateTimeInputState> {

    state: ReactDateTimeInputState = {
        month: '', day: '', year: '',
        hour: '', minute: '', second: '',
        ampm: '',
        calendarOpen: false,
        calYear: new Date().getFullYear(),
        calMonth: new Date().getMonth() + 1,
    };

    private inputRefs: { [K in SegmentKey]?: HTMLInputElement | null } = {};
    private ampmAutoFilled = false;
    private wrapperRef: HTMLSpanElement | null = null;

    componentDidMount(): void {
        this.parseValueToState(this.props.value);
        document.addEventListener('mousedown', this.handleDocumentMouseDown, true);
    }

    componentWillUnmount(): void {
        document.removeEventListener('mousedown', this.handleDocumentMouseDown, true);
    }

    /** Close the calendar popup if the user clicks outside the wrapper. */
    private handleDocumentMouseDown = (e: MouseEvent) => {
        if (!this.state.calendarOpen) return;
        const target = e.target as Node | null;
        if (this.wrapperRef && target && !this.wrapperRef.contains(target)) {
            this.setState({ calendarOpen: false });
        }
    };

    componentDidUpdate(prevProps: ReactDateTimeInputProps): void {
        // When the parent's value changes (and isn't just our own
        // round-trip), re-sync the segment state.
        if (prevProps.value !== this.props.value) {
            const wire = this.buildWireValue();
            if (wire !== (this.props.value || '')) {
                this.parseValueToState(this.props.value);
            }
        }
    }

    private static pad2(n: number): string {
        return n < 10 ? '0' + n : '' + n;
    }

    /** True when value looks like a Unix epoch number (10+ digit string or actual number). */
    private static isEpochValue(value: any): boolean {
        if (typeof value === 'number' && isFinite(value)) return true;
        if (typeof value !== 'string') return false;
        const v = value.trim();
        if (v === '' || !/^-?\d+$/.test(v)) return false;
        return v.replace(/^-/, '').length >= 10;
    }

    private static parseWireToDate(value: string, kind: DateTimeKind): Date | null {
        if (value === null || value === undefined || value === '') return null;

        if (ReactDateTimeInput.isEpochValue(value)) {
            let n = typeof value === 'number' ? value : parseInt(String(value), 10);
            if (!isFinite(n)) return null;
            const abs = Math.abs(n);
            if (abs > 0 && abs < 1e12) n = n * 1000;
            const d = new Date(n);
            return isNaN(d.getTime()) ? null : d;
        }

        const v = String(value).trim();
        if (kind === 'time') {
            const now = new Date();
            const pad = ReactDateTimeInput.pad2;
            const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
            const t = v.length === 5 ? v + ':00' : v;
            const d = new Date(`${today}T${t}`);
            return isNaN(d.getTime()) ? null : d;
        }

        const cleaned = v
            .replace(' ', 'T')
            .replace(/Z$/, '')
            .replace(/[+-]\d{2}:?\d{2}$/, '');
        const d = new Date(cleaned);
        return isNaN(d.getTime()) ? null : d;
    }

    private isRangeOutOfOrder(): boolean {
        const role = this.props.rangeRole;
        if (role !== 'start' && role !== 'end') return false;

        const otherRaw = this.props.rangeCounterpartValue;
        if (!otherRaw) return false;

        const kind = this.props.kind || 'dateTime';
        const myWire = this.buildWireValue();
        if (!myWire) return false; // our own value isn't a complete, valid date yet

        const myDate = ReactDateTimeInput.parseWireToDate(myWire, kind);
        const otherDate = ReactDateTimeInput.parseWireToDate(otherRaw, kind);
        if (!myDate || !otherDate) return false;

        const coarse = kind === 'date';
        const toCmp = (d: Date) => coarse
            ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
            : d.getTime();

        const mine = toCmp(myDate);
        const theirs = toCmp(otherDate);

        if (role === 'start') return mine > theirs;     // start after end
        return mine < theirs;                            // end before start
    }

    private parseValueToState(value: string): void {
        type Segments = Pick<ReactDateTimeInputState,
            'month' | 'day' | 'year' | 'hour' | 'minute' | 'second' | 'ampm'>;
        const empty: Segments = {
            month: '', day: '', year: '',
            hour: '', minute: '', second: '', ampm: '',
        };

        if (value === null || value === undefined || value === '') {
            this.setState(empty);
            return;
        }

        const kind = this.props.kind || 'dateTime';
        let d: Date | null = null;

        if (ReactDateTimeInput.isEpochValue(value)) {
            let n = typeof value === 'number' ? value : parseInt(String(value), 10);
            if (isFinite(n)) {
                const abs = Math.abs(n);
                if (abs > 0 && abs < 1e12) n = n * 1000;
                const tmp = new Date(n);
                if (!isNaN(tmp.getTime())) d = tmp;
            }
        } else {
            const v = String(value).trim();
            if (kind === 'time') {
                // Anchor to today so we can use Date math.
                const now = new Date();
                const pad = ReactDateTimeInput.pad2;
                const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
                const t = v.length === 5 ? v + ':00' : v;
                const tmp = new Date(`${today}T${t}`);
                if (!isNaN(tmp.getTime())) d = tmp;
            } else {
                const cleaned = v
                    .replace(' ', 'T')
                    .replace(/Z$/, '')
                    .replace(/[+-]\d{2}:?\d{2}$/, '');
                const tmp = new Date(cleaned);
                if (!isNaN(tmp.getTime())) d = tmp;
            }
        }

        if (!d) {
            this.setState(empty);
            return;
        }

        const pad = ReactDateTimeInput.pad2;
        const next: Segments = { ...empty };

        if (kind === 'date' || kind === 'dateTime') {
            next.month = pad(d.getMonth() + 1);
            next.day = pad(d.getDate());
            next.year = String(d.getFullYear());
        }
        if (kind === 'time' || kind === 'dateTime') {
            const military = this.props.militaryTime === true;
            const h24 = d.getHours();
            if (military) {
                next.hour = pad(h24);
                next.ampm = '';
            } else {
                let h = h24;
                const isPM = h >= 12;
                if (h === 0) h = 12;
                else if (h > 12) h -= 12;
                next.hour = pad(h);
                next.ampm = isPM ? 'PM' : 'AM';
            }
            next.minute = pad(d.getMinutes());
            next.second = pad(d.getSeconds());
        }

        this.setState(next);
    }

    private buildWireValue(state?: ReactDateTimeInputState): string {
        const s = state || this.state;
        const kind = this.props.kind || 'dateTime';
        const { month, day, year, hour, minute, second, ampm } = s;

        const military = this.props.militaryTime === true;
        if (kind === 'date' || kind === 'dateTime') {
            if (!month || !day || !year) return '';
        }
        if (kind === 'time' || kind === 'dateTime') {
            if (!hour || !minute || !second) return '';
            if (!military && !ampm) return '';
        }

        const segs: SegmentKey[] = this.segmentOrder(kind);
        for (const seg of segs) {
            if (this.isSegmentInvalid(seg)) return '';
        }

        const m = parseInt(month || '1', 10);
        const dd = parseInt(day || '1', 10);
        const y = parseInt(year || '1970', 10);
        let h24 = parseInt(hour || '0', 10);
        if (!military) {
            if (ampm === 'PM' && h24 !== 12) h24 += 12;
            else if (ampm === 'AM' && h24 === 12) h24 = 0;
        }
        const mi = parseInt(minute || '0', 10);
        const se = parseInt(second || '0', 10);

        let d: Date;
        if (kind === 'date') {
            d = new Date(y, m - 1, dd, 0, 0, 0, 0);
        } else if (kind === 'time') {
            const now = new Date();
            d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h24, mi, se, 0);
        } else {
            d = new Date(y, m - 1, dd, h24, mi, se, 0);
        }

        if (isNaN(d.getTime())) return '';
        // Reject silent date wrap (e.g. Feb 30 → Mar 2) for date kinds.
        if ((kind === 'date' || kind === 'dateTime')
            && (d.getFullYear() !== y
                || d.getMonth() !== m - 1
                || d.getDate() !== dd)) {
            return '';
        }
        return String(d.getTime());
    }

    private segmentOrder(kind?: DateTimeKind): SegmentKey[] {
        const k = kind || this.props.kind || 'dateTime';
        const military = this.props.militaryTime === true;
        if (k === 'date') return ['month', 'day', 'year'];
        if (k === 'time') {
            return military
                ? ['hour', 'minute', 'second']
                : ['hour', 'minute', 'second', 'ampm'];
        }
        return military
            ? ['month', 'day', 'year', 'hour', 'minute', 'second']
            : ['month', 'day', 'year', 'hour', 'minute', 'second', 'ampm'];
    }

    private static maxLengthFor(seg: SegmentKey): number {
        return seg === 'year' ? 4 : seg === 'ampm' ? 2 : 2;
    }

    private focusSegment(seg: SegmentKey): void {
        const ref = this.inputRefs[seg];
        if (ref) {
            ref.focus();
            ref.select();
        }
    }

    private focusNextSegment(current: SegmentKey): void {
        const order = this.segmentOrder();
        const idx = order.indexOf(current);
        if (idx >= 0 && idx < order.length - 1) this.focusSegment(order[idx + 1]);
    }

    private focusPrevSegment(current: SegmentKey): void {
        const order = this.segmentOrder();
        const idx = order.indexOf(current);
        if (idx > 0) this.focusSegment(order[idx - 1]);
    }

    private commit(nextState: Partial<ReactDateTimeInputState>): void {
        this.setState(nextState as ReactDateTimeInputState, () => {
            const wire = this.buildWireValue();
            if (this.props.onChange) this.props.onChange(wire);
        });
    }

    private handleNumericChange = (seg: Exclude<SegmentKey, 'ampm'>) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let v = e.target.value;
            if (!/^\d*$/.test(v)) return;
            const max = ReactDateTimeInput.maxLengthFor(seg);
            if (v.length > max) v = v.substring(0, max);

            let convertedAmpm: 'AM' | 'PM' | null = null;
            if (seg === 'hour'
                && this.props.allowMilitaryTimeEntry === true
                && this.props.militaryTime !== true
                && v.length === max) {
                const n = parseInt(v, 10);
                if (n === 0) {
                    v = '12';
                    convertedAmpm = 'AM';
                } else if (n >= 13 && n <= 23) {
                    v = ReactDateTimeInput.pad2(n - 12);
                    convertedAmpm = 'PM';
                }
            }

            const patch: Partial<ReactDateTimeInputState> = { [seg]: v } as Partial<ReactDateTimeInputState>;
            if (convertedAmpm) {
                (patch as any).ampm = convertedAmpm;
                this.ampmAutoFilled = false;
            }
            this.setState(patch as unknown as ReactDateTimeInputState, () => {
                const wire = this.buildWireValue();
                if (this.props.onChange) this.props.onChange(wire);
                if (v.length === max) {
                    this.focusNextSegment(seg);
                }
            });
        };

    private handleAmPmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = (e.target.value || '').toUpperCase().replace(/[^AP M]/g, '');
        let v = '';
        if (raw.startsWith('A')) v = 'AM';
        else if (raw.startsWith('P')) v = 'PM';
        this.ampmAutoFilled = false;
        this.commit({ ampm: v });
    };

    private handleSegmentKeyDown = (seg: SegmentKey) =>
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const target = e.currentTarget;
            const atStart = target.selectionStart === 0 && target.selectionEnd === 0;
            const atEnd = target.selectionStart === target.value.length
                       && target.selectionEnd === target.value.length;

            if (e.key === 'Tab') {
                const order = this.segmentOrder();
                const idx = order.indexOf(seg);
                if (e.shiftKey) {
                    if (idx > 0) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.focusPrevSegment(seg);
                    }
                } else {
                    if (idx < order.length - 1) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.focusNextSegment(seg);
                    }
                }
                return;
            }

            if (e.key === 'ArrowRight') {
                if (atEnd || seg === 'ampm') {
                    e.preventDefault();
                    this.focusNextSegment(seg);
                }
            } else if (e.key === 'ArrowLeft') {
                if (atStart || seg === 'ampm') {
                    e.preventDefault();
                    this.focusPrevSegment(seg);
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.bumpSegment(seg, +1);
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.bumpSegment(seg, -1);
            } else if (e.key === 'Backspace') {
                if (seg === 'ampm') {
                    e.preventDefault();
                    this.ampmAutoFilled = false;
                    if (this.state.ampm) {
                        this.commit({ ampm: '' });
                    } else {
                        this.focusPrevSegment(seg);
                    }
                } else if (target.value === '' && seg !== this.segmentOrder()[0]) {
                    e.preventDefault();
                    this.focusPrevSegment(seg);
                }
            } else if (seg === 'ampm') {
                if (e.key === 'a' || e.key === 'A') {
                    e.preventDefault();
                    this.ampmAutoFilled = false;
                    this.commit({ ampm: 'AM' });
                } else if (e.key === 'p' || e.key === 'P') {
                    e.preventDefault();
                    this.ampmAutoFilled = false;
                    this.commit({ ampm: 'PM' });
                } else if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    this.ampmAutoFilled = false;
                    this.commit({ ampm: this.state.ampm === 'PM' ? 'AM' : 'PM' });
                } else if (e.key.length === 1 && /[0-9]/.test(e.key)) {
                    // Block stray digit input on the AM/PM segment.
                    e.preventDefault();
                }
            }
        };

    private bumpSegment(seg: SegmentKey, delta: number): void {
        if (seg === 'ampm') {
            this.ampmAutoFilled = false;
            this.commit({ ampm: this.state.ampm === 'PM' ? 'AM' : 'PM' });
            return;
        }
        const cur = parseInt(this.state[seg] || '0', 10) || 0;
        let min = 1;
        let max = 12;
        if (seg === 'day') { min = 1; max = 31; }
        else if (seg === 'year') { min = 1; max = 3000; }
        else if (seg === 'hour') {
            if (this.props.militaryTime) { min = 0; max = 23; }
            else { min = 1; max = 12; }
        }
        else if (seg === 'minute' || seg === 'second') { min = 0; max = 59; }

        let next = cur + delta;
        if (next > max) next = min;
        else if (next < min) next = max;

        const padded = seg === 'year'
            ? String(next).padStart(4, '0')
            : ReactDateTimeInput.pad2(next);
        this.commit({ [seg]: padded } as Partial<ReactDateTimeInputState>);
    }

    private otherSegmentsValid(excludeSeg: SegmentKey): boolean {
        const order = this.segmentOrder();
        for (const seg of order) {
            if (seg === excludeSeg) continue;
            const v = this.state[seg];
            if (!v) return false;
            if (this.isSegmentInvalid(seg)) return false;
        }
        return true;
    }

    private handleSegmentFocus = (seg: SegmentKey) =>
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Select-all on focus so typing replaces, not appends.
            e.target.select();

            if (this.state.calendarOpen) {
                this.setState({ calendarOpen: false });
            }

            if (seg === 'ampm' && !this.props.disabled
                && !this.state.ampm
                && this.otherSegmentsValid('ampm')) {
                this.ampmAutoFilled = true;
                this.setState({ ampm: 'AM' } as ReactDateTimeInputState, () => {
                    const ref = this.inputRefs.ampm;
                    if (ref && document.activeElement === ref) ref.select();
                    const wire = this.buildWireValue();
                    if (this.props.onChange) this.props.onChange(wire);
                });
            }
        };

    private handleSegmentBlur = (seg: SegmentKey) =>
        (_e: React.FocusEvent<HTMLInputElement>) => {
            if (seg !== 'ampm' && seg !== 'year') {
                const v = this.state[seg];
                if (v && v.length === 1) {
                    this.commit({ [seg]: '0' + v } as Partial<ReactDateTimeInputState>);
                }
            }

            if (seg === 'ampm'
                && this.ampmAutoFilled
                && !this.otherSegmentsValid('ampm')) {
                this.ampmAutoFilled = false;
                this.setState({ ampm: '' } as ReactDateTimeInputState, () => {
                    const wire = this.buildWireValue();
                    if (this.props.onChange) this.props.onChange(wire);
                });
            }

            window.setTimeout(() => {
                if (this.props.onBlur) this.props.onBlur(this.buildWireValue());
            }, 0);
        };

    private handleCalendarClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.props.disabled) return;
        const wantOpen = !this.state.calendarOpen;
        if (wantOpen) {
            // Initialize the visible month/year to whatever the segments
            // currently hold, falling back to today.
            const now = new Date();
            const m = parseInt(this.state.month, 10);
            const y = parseInt(this.state.year, 10);
            this.setState({
                calendarOpen: true,
                calMonth: (m >= 1 && m <= 12) ? m : (now.getMonth() + 1),
                calYear: (y >= 1 && y <= 3000) ? y : now.getFullYear(),
            });
        } else {
            this.setState({ calendarOpen: false });
        }
    };

    private handleCalendarMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ calMonth: parseInt(e.target.value, 10) });
    };

    private handleCalendarYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ calYear: parseInt(e.target.value, 10) });
    };

    private handleCalendarPrevMonth = () => {
        let m = this.state.calMonth - 1;
        let y = this.state.calYear;
        if (m < 1) { m = 12; y -= 1; }
        this.setState({ calMonth: m, calYear: y });
    };

    private handleCalendarNextMonth = () => {
        let m = this.state.calMonth + 1;
        let y = this.state.calYear;
        if (m > 12) { m = 1; y += 1; }
        this.setState({ calMonth: m, calYear: y });
    };

    private handleCalendarDayClick = (day: number, year?: number, month?: number) => {
        const pad = ReactDateTimeInput.pad2;
        const y = (year != null) ? year : this.state.calYear;
        const m = (month != null) ? month : this.state.calMonth;
        this.setState({
            year: String(y),
            month: pad(m),
            day: pad(day),
            calendarOpen: false,
        }, () => {
            const wire = this.buildWireValue();
            if (this.props.onChange) this.props.onChange(wire);
            // After picking from the popup, push focus into the first
            // time segment (or year, for date-only) so the user can
            // continue typing.
            const kind = this.props.kind || 'dateTime';
            if (kind === 'dateTime') this.focusSegment('hour');
        });
    };

    private handleCalendarToday = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const now = new Date();
        // Realign visible month/year to today AND pick today's date.
        this.setState({
            calMonth: now.getMonth() + 1,
            calYear: now.getFullYear(),
        });
        this.handleCalendarDayClick(now.getDate(), now.getFullYear(), now.getMonth() + 1);
    };

    private renderMonthPanel(year: number, month: number, monthNames: string[]): React.ReactNode {
        const firstWeekday = new Date(year, month - 1, 1).getDay(); // 0 = Sunday
        const daysInMonth = new Date(year, month, 0).getDate();
        const cells: Array<{ day: number | null }> = [];
        for (let i = 0; i < firstWeekday; i++) cells.push({ day: null });
        for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d });
        while (cells.length % 7 !== 0) cells.push({ day: null });

        const today = new Date();
        const todayMatch = today.getFullYear() === year
            && today.getMonth() + 1 === month;
        const selMonth = parseInt(this.state.month, 10);
        const selYear = parseInt(this.state.year, 10);
        const selDay = parseInt(this.state.day, 10);
        const selectedMatch = selYear === year && selMonth === month;

        const dayCellStyle = (highlighted: boolean, isSelected: boolean): React.CSSProperties => ({
            width: '28px',
            height: '24px',
            border: '1px solid transparent',
            background: isSelected ? '#1976d2'
                : highlighted ? '#e3f2fd' : 'transparent',
            color: isSelected ? '#fff' : 'inherit',
            cursor: 'pointer',
            borderRadius: '3px',
            fontSize: '12px',
            padding: 0,
            boxSizing: 'border-box',
        });

        const headerCellStyle: React.CSSProperties = {
            width: '28px',
            height: '20px',
            textAlign: 'center',
            fontSize: '11px',
            color: '#333',
            fontWeight: 700,
        };

        return (
            <div>
                {/* Weekday header row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 28px)', gap: '2px' }}>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                        <div key={i} style={headerCellStyle}><b>{d}</b></div>
                    ))}
                </div>
                {/* Day grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 28px)', gap: '2px' }}>
                    {cells.map((c, i) => {
                        if (c.day === null) {
                            return <div key={i} style={{ width: '28px', height: '24px' }} />;
                        }
                        const isToday = todayMatch && c.day === today.getDate();
                        const isSelected = selectedMatch && c.day === selDay;
                        const day = c.day;
                        return (
                            <button
                                key={i}
                                type="button"
                                className={'ecp-cal-btn ecp-cal-day-btn'
                                    + (isSelected ? ' ecp-cal-day-selected' : '')}
                                onClick={() => this.handleCalendarDayClick(day, year, month)}
                                style={dayCellStyle(isToday, isSelected)}
                            >{day}</button>
                        );
                    })}
                </div>
            </div>
        );
    }

    private renderCalendarPopup(): React.ReactNode {
        const { calMonth, calYear } = this.state;
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
        ];
        // Year list: ±50 years around the visible year (or selected year).
        const baseYear = calYear;
        const years: number[] = [];
        for (let y = baseYear - 50; y <= baseYear + 50; y++) years.push(y);

        // Compute the right-panel's month/year (current + 1 month).
        let nextMonth = calMonth + 1;
        let nextYear = calYear;
        if (nextMonth > 12) { nextMonth = 1; nextYear += 1; }

        const chevronBtnStyle: React.CSSProperties = {
            border: '1px solid #ccc',
            background: '#f5f5f5',
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '11px',
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
        };

        return (
            <div
                className="ecp-datetime-calendar-popup"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    background: '#fff',
                    border: '1px solid #aaa',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    padding: '8px',
                    zIndex: 1000,
                    fontSize: '12px',
                    fontFamily: 'inherit',
                }}
            >
                <div
                    className={this.props.calendarHeaderClassName !== undefined
                        ? this.props.calendarHeaderClassName
                        : 'ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-left'}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginBottom: '6px',
                        ...(this.props.calendarHeaderStyle || {}),
                    }}
                >
                    <button
                        type="button"
                        className="ecp-cal-btn"
                        title="Previous month"
                        onClick={this.handleCalendarPrevMonth}
                        style={chevronBtnStyle}
                    >
                        <svg
                            aria-hidden="true"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <select
                        value={calMonth}
                        onChange={this.handleCalendarMonthChange}
                        style={{ fontSize: '12px', padding: '2px' }}
                    >
                        {monthNames.map((name, i) => (
                            <option key={i + 1} value={i + 1}>{name}</option>
                        ))}
                    </select>
                    <select
                        value={calYear}
                        onChange={this.handleCalendarYearChange}
                        style={{ width: '70px', fontSize: '12px', padding: '2px' }}
                    >
                        {years.map((y) => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <div style={{ flex: 1 }} />
                    <span style={{
                        fontWeight: 'bold',
                        color: '#333',
                        padding: '0 4px',
                    }}>
                        {monthNames[nextMonth - 1]} {nextYear}
                    </span>
                    <button
                        type="button"
                        className="ecp-cal-btn"
                        title="Next month"
                        onClick={this.handleCalendarNextMonth}
                        style={chevronBtnStyle}
                    >
                        <svg
                            aria-hidden="true"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                    {this.renderMonthPanel(calYear, calMonth, monthNames)}
                    {this.renderMonthPanel(nextYear, nextMonth, monthNames)}
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '6px',
                }}>
                    <button
                        type="button"
                        className="ecp-cal-btn"
                        onClick={this.handleCalendarToday}
                        style={{
                            border: '1px solid #ccc',
                            background: '#f5f5f5',
                            cursor: 'pointer',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            fontSize: '11px',
                        }}
                    >Today</button>
                </div>
            </div>
        );
    }

    private handleNowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.props.disabled) return;

        const now = new Date();
        const kind = this.props.kind || 'dateTime';
        const pad = ReactDateTimeInput.pad2;
        type Segments = Pick<ReactDateTimeInputState,
            'month' | 'day' | 'year' | 'hour' | 'minute' | 'second' | 'ampm'>;
        const next: Segments = {
            month: '', day: '', year: '',
            hour: '', minute: '', second: '', ampm: '',
        };

        if (kind === 'date' || kind === 'dateTime') {
            next.month = pad(now.getMonth() + 1);
            next.day = pad(now.getDate());
            next.year = String(now.getFullYear());
        }
        if (kind === 'time' || kind === 'dateTime') {
            const h24 = now.getHours();
            if (this.props.militaryTime) {
                next.hour = pad(h24);
                next.ampm = '';
            } else {
                let h = h24;
                const isPM = h >= 12;
                if (h === 0) h = 12;
                else if (h > 12) h -= 12;
                next.hour = pad(h);
                next.ampm = isPM ? 'PM' : 'AM';
            }
            next.minute = pad(now.getMinutes());
            next.second = pad(now.getSeconds());
        }

        this.setState({ ...next, calendarOpen: false } as ReactDateTimeInputState, () => {
            const wire = this.buildWireValue();
            if (this.props.onChange) this.props.onChange(wire);
            if (this.props.onBlur) this.props.onBlur(wire);
        });
    };

    private handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.props.disabled) return;

        this.setState({
            month: '', day: '', year: '',
            hour: '', minute: '', second: '', ampm: '',
            calendarOpen: false,
        } as ReactDateTimeInputState, () => {
            if (this.props.onChange) this.props.onChange('');
            if (this.props.onBlur) this.props.onBlur('');
        });
    };

    private isSegmentInvalid(seg: SegmentKey): boolean {
        const v = this.state[seg];
        if (!v) return false; // empty handled elsewhere

        if (seg === 'ampm') {
            return v !== 'AM' && v !== 'PM';
        }

        const n = parseInt(v, 10);
        if (!isFinite(n)) return true;

        const isPartial = v.length < 2;

        switch (seg) {
            case 'month':  return isPartial ? false : (n < 1 || n > 12);
            case 'hour':
                if (isPartial) return false;
                return this.props.militaryTime
                    ? (n < 0 || n > 23)
                    : (n < 1 || n > 12);
            case 'minute': return isPartial ? false : (n < 0 || n > 59);
            case 'second': return isPartial ? false : (n < 0 || n > 59);
            case 'year': {
                // Don't flag year as invalid until the user has typed
                // all 4 digits — otherwise typing "2" on the way to
                // "2025" would briefly turn the field red. Once it's
                // complete, require 1-3000 (3000 is "far enough" in
                // the future for any sane datetime entry).
                if (v.length < 4) return false;
                return n < 1900 || n > 3000;
            }
            case 'day': {
                if (isPartial) return false;
                if (n < 1 || n > 31) return true;
                // Calendar-aware check when month + year are filled.
                const m = parseInt(this.state.month, 10);
                const y = parseInt(this.state.year, 10);
                if (m >= 1 && m <= 12 && y >= 1 && y <= 3000) {
                    const daysInMonth = new Date(y, m, 0).getDate();
                    if (n > daysInMonth) return true;
                }
                return false;
            }
            default: return false;
        }
    }

    private renderNumericSegment(
        seg: Exclude<SegmentKey, 'ampm'>,
        widthEm: number,
        placeholder: string,
    ): React.ReactNode {
        ensureSegmentStylesInjected();
        const value = this.state[seg];
        const invalid = this.isSegmentInvalid(seg);
        return (
            <input
                ref={(el) => { this.inputRefs[seg] = el; }}
                type="text"
                inputMode="numeric"
                className="ecp-datetime-segment"
                title={invalid ? this.invalidTitleFor(seg) : undefined}
                value={value}
                disabled={this.props.disabled}
                maxLength={ReactDateTimeInput.maxLengthFor(seg)}
                placeholder={placeholder}
                onChange={this.handleNumericChange(seg)}
                onKeyDown={this.handleSegmentKeyDown(seg)}
                onFocus={this.handleSegmentFocus(seg)}
                onBlur={this.handleSegmentBlur(seg)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: widthEm + 'em',
                    minWidth: widthEm + 'em',
                    padding: '2px 0',
                    border: invalid ? '1px solid #c33' : '1px solid transparent',
                    borderRadius: '2px',
                    outline: 'none',
                    textAlign: 'center',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    background: invalid ? '#fff0f0' : 'transparent',
                    color: invalid
                        ? '#c33'
                        : this.props.disabled ? '#999' : 'inherit',
                }}
            />
        );
    }

    private renderAmPmSegment(): React.ReactNode {
        ensureSegmentStylesInjected();
        const value = this.state.ampm;
        const isEmpty = !value;
        const invalid = this.isSegmentInvalid('ampm');
        return (
            <input
                ref={(el) => { this.inputRefs.ampm = el; }}
                type="text"
                className="ecp-datetime-segment"
                title={invalid ? 'AM/PM must be AM or PM' : undefined}
                value={isEmpty ? '' : value}
                disabled={this.props.disabled}
                maxLength={2}
                // Native placeholder shows the grayed AM hint when empty.
                placeholder="AM"
                onChange={this.handleAmPmChange}
                onKeyDown={this.handleSegmentKeyDown('ampm')}
                onFocus={this.handleSegmentFocus('ampm')}
                onBlur={this.handleSegmentBlur('ampm')}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '2.2em',
                    minWidth: '2.2em',
                    padding: '2px 0',
                    border: invalid ? '1px solid #c33' : '1px solid transparent',
                    borderRadius: '2px',
                    outline: 'none',
                    textAlign: 'center',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    background: invalid ? '#fff0f0' : 'transparent',
                    color: invalid
                        ? '#c33'
                        : this.props.disabled ? '#999' : 'inherit',
                    textTransform: 'uppercase',
                }}
            />
        );
    }

    private invalidTitleFor(seg: SegmentKey): string {
        switch (seg) {
            case 'month':  return 'Month must be 1-12';
            case 'day':    return 'Day is out of range for this month';
            case 'year':   return 'Year must be 1900-3000';
            case 'hour':   return this.props.militaryTime ? 'Hour must be 0-23' : 'Hour must be 1-12';
            case 'minute': return 'Minute must be 0-59';
            case 'second': return 'Second must be 0-59';
            case 'ampm':   return 'AM/PM must be AM or PM';
            default:       return 'Invalid value';
        }
    }

    render(): React.ReactNode {
        ensureSegmentStylesInjected();
        const kind = this.props.kind || 'dateTime';
        const { month, day, year, hour, minute, second, ampm } = this.state;

        const hasAny =
            !!month || !!day || !!year ||
            !!hour || !!minute || !!second || !!ampm;
        const isEmpty = !hasAny;
        const showInvalid = !this.props.disabled && this.props.required && isEmpty;

        // Range-pair check: red border + tooltip when start > end.
        const rangeBad = !this.props.disabled && this.isRangeOutOfOrder();
        const rangeTooltip = rangeBad
            ? (this.props.rangeInvalidTitle
                || (this.props.rangeRole === 'start'
                    ? 'Start date/time is after the end date/time'
                    : 'End date/time is before the start date/time'))
            : undefined;

        const combinedTitle = rangeTooltip ? rangeTooltip : this.props.title;

        const wrapperClass = 'ecp-datetime-wrapper'
            + (this.props.css ? ' ' + this.props.css : '')
            + (showInvalid ? ' invalidInput' : '')
            + (rangeBad ? ' ecp-datetime-range-invalid invalidInput' : '');

        const sepStyle: React.CSSProperties = {
            color: '#666',
            userSelect: 'none',
            padding: 0,
            margin: 0,
        };

        const showRedBorder = showInvalid || rangeBad;
        const fieldBoxStyle: React.CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            border: showRedBorder ? '1px solid #c33' : '1px solid #aaa',
            borderRadius: '3px',
            padding: '1px 4px',
            background: this.props.disabled
                ? '#f3f3f3'
                : rangeBad ? '#fff6f6' : '#fff',
            height: '22px',
            boxSizing: 'border-box',
            fontSize: 'inherit',
            ...(this.props.style || {}),
        };

        return (
            <span
                ref={(el) => { this.wrapperRef = el; }}
                id={this.props.id}
                className={wrapperClass}
                title={combinedTitle}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                }}
            >
                <span className="ecp-datetime-fieldbox" style={fieldBoxStyle}>
                    {(kind === 'date' || kind === 'dateTime') && (
                        <>
                            {this.renderNumericSegment('month', 2.3, 'MM')}
                            <span style={sepStyle}>/</span>
                            {this.renderNumericSegment('day', 2.3, 'DD')}
                            <span style={sepStyle}>/</span>
                            {this.renderNumericSegment('year', 3.8, 'YYYY')}
                        </>
                    )}
                    {(kind === 'date' || kind === 'dateTime') && (
                        <button
                            type="button"
                            className="ecp-datetime-calendar-btn"
                            tabIndex={-1}
                            title="Open calendar"
                            disabled={this.props.disabled}
                            onClick={this.handleCalendarClick}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                marginLeft: '4px',
                                marginRight: kind === 'dateTime' ? '6px' : '0',
                                padding: '0 4px',
                                background: 'transparent',
                                border: 'none',
                                cursor: this.props.disabled ? 'default' : 'pointer',
                                lineHeight: 1,
                                color: this.props.disabled ? '#bbb' : '#555',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                            aria-label="Open calendar"
                        >
                            <svg
                                aria-hidden="true"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </button>
                    )}
                    {(kind === 'time' || kind === 'dateTime') && (
                        <>
                            {this.renderNumericSegment('hour', 2.3, 'HH')}
                            <span style={sepStyle}>:</span>
                            {this.renderNumericSegment('minute', 2.3, 'mm')}
                            <span style={sepStyle}>:</span>
                            {this.renderNumericSegment('second', 2.3, 'ss')}
                            {!this.props.militaryTime && (
                                <>
                                    <span aria-hidden="true" style={{ display: 'inline-block', width: '4px' }} />
                                    {this.renderAmPmSegment()}
                                </>
                            )}
                        </>
                    )}
                    {this.state.calendarOpen && (kind === 'date' || kind === 'dateTime') && (
                        this.renderCalendarPopup()
                    )}
                </span>

                <button
                    type="button"
                    className="ecp-datetime-now-btn"
                    title={kind === 'date'
                        ? 'Set to today'
                        : kind === 'time'
                            ? 'Set to current time'
                            : 'Set to current date / time'}
                    disabled={this.props.disabled}
                    onClick={this.handleNowClick}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        padding: '2px 8px',
                        fontSize: '9pt',
                        cursor: this.props.disabled ? 'default' : 'pointer',
                        lineHeight: 1,
                        height: '22px',
                    }}
                >
                    Now
                </button>
                {this.props.showClearButton && (
                    <button
                        type="button"
                        className="ecp-datetime-clear-btn"
                        title={kind === 'date'
                            ? 'Clear date'
                            : kind === 'time'
                                ? 'Clear time'
                                : 'Clear date / time'}
                        disabled={this.props.disabled || isEmpty}
                        onClick={this.handleClearClick}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                            padding: '2px 8px',
                            fontSize: '9pt',
                            cursor: (this.props.disabled || isEmpty) ? 'default' : 'pointer',
                            lineHeight: 1,
                            height: '22px',
                        }}
                    >
                        Clear
                    </button>
                )}
            </span>
        );
    }
}


